import {
  Box,
  RoutineOccasionInlineStatusCircleRelay,
  TouchableBox,
} from "arkh";
import React, { useCallback } from "react";
import { Platform } from "react-native";
import { graphql, useFragment, useMutation } from "react-relay";
import { WidgetTeamScheduleInlineRoutineListItemMutation } from "./__generated__/WidgetTeamScheduleInlineRoutineListItemMutation.graphql";
import { WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion$key } from "./__generated__/WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion.graphql";
import * as Haptics from "expo-haptics";
import { WidgetTeamScheduleRoutineListItemContent } from "./WidgetTeamScheduleRoutineListItemContent";
import { WidgetTeamScheduleRoutineIssueIndicator } from "./WidgetTeamScheduleRoutineIssueIndicator";
import { useEmployeeId } from "../EmployeeId";

export function WidgetTeamScheduleInlineRoutineListItem(props: {
  onPress?(routineId: string, sequenceNumber: number): void;
  onAssigneesPress?(
    routineId: string,
    sequenceNumber: number,
    routineOccasionId: string
  ): void;
  inlineRoutineOccasion: WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion$key;
  status: "active" | "soon" | "none";
  freq: "DAILY" | "WEEKLY" | "MONTHLY";
  size: "COMPACT" | "DEFAULT" | "LARGE";
}) {
  const inlineRoutineOccasion = useFragment(
    graphql`
      fragment WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion on TeamScheduleInlineRoutineOccasion {
        id
        taskExecution {
          id
          task {
            id
          }
          sectionId
          routineOccasion {
            id
            store {
              myRole
            }
            sequenceNumber
            ...WidgetTeamScheduleRoutineListItemContent_RoutineOccasion
            ...WidgetTeamScheduleRoutineIssueIndicator_routineOccasion
            ...RoutineOccasionStatusCircleInline_RoutineOccasion
          }
          routine {
            id
            ...WidgetTeamScheduleRoutineListItemContent_Routine
          }
          checkValue
        }
      }
    `,
    props.inlineRoutineOccasion
  );

  const [setCheckValue] =
    useMutation<WidgetTeamScheduleInlineRoutineListItemMutation>(graphql`
      mutation WidgetTeamScheduleInlineRoutineListItemMutation(
        $taskExecutionId: ID!
        $value: TaskSentiment
        $employeeId: ID
      ) @raw_response_type {
        setTaskExecutionCheck(
          id: $taskExecutionId
          sentiment: $value
          employeeId: $employeeId
        ) {
          id
          checkValue
          isCompleted
          routineOccasion {
            status(calculateNoTasks: true, calculateNotCompletedStarted: true)
          }
        }
      }
    `);

  const currentEmployeeId = useEmployeeId();
  const onToggle = useCallback(() => {
    if (Platform.OS === "ios") {
      Haptics.impactAsync();
    }
    const val =
      inlineRoutineOccasion.taskExecution.checkValue === "POSITIVE"
        ? null
        : "POSITIVE";
    if (!inlineRoutineOccasion.taskExecution.routine) {
      return;
    }

    setCheckValue({
      variables: {
        taskExecutionId: inlineRoutineOccasion.taskExecution.id,
        employeeId: currentEmployeeId,
        value: val,
      },
      optimisticResponse:
        val === "POSITIVE"
          ? {
              setTaskExecutionCheck: {
                checkValue: val,
                id: inlineRoutineOccasion.taskExecution.id,
                isCompleted: true,
                routineOccasion: {
                  id: inlineRoutineOccasion.taskExecution.routineOccasion.id,
                  status: "COMPLETED",
                },
              },
            }
          : undefined,
    });
  }, [currentEmployeeId, inlineRoutineOccasion.taskExecution, setCheckValue]);

  const { onPress } = props;

  const op = useCallback(() => {
    if (inlineRoutineOccasion.taskExecution.routine) {
      onPress?.(
        inlineRoutineOccasion.taskExecution.routine?.id,
        inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber
      );
    }
  }, [
    inlineRoutineOccasion.taskExecution.routine,
    inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber,
    onPress,
  ]);
  const onAssigneesPress = props.onAssigneesPress
    ? () => {
        if (
          props.onAssigneesPress &&
          inlineRoutineOccasion.taskExecution.routine?.id &&
          inlineRoutineOccasion.taskExecution.routineOccasion
        ) {
          props.onAssigneesPress(
            inlineRoutineOccasion.taskExecution.routine?.id,
            inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber,
            inlineRoutineOccasion.taskExecution.routineOccasion.id
          );
        }
      }
    : undefined;

  return (
    <TouchableBox
      style={{
        flex: 1,
        flexDirection: "row",
        justifyContent: "space-between",
        alignContent: "center",
        alignItems: "center",
        height: "100%",
      }}
      onPress={op}
      disabled={!props.onPress}
      spaceRight="small"
    >
      <WidgetTeamScheduleRoutineListItemContent
        status={props.status}
        routine={inlineRoutineOccasion.taskExecution.routine!}
        size={props.size}
        routineOccasion={inlineRoutineOccasion.taskExecution.routineOccasion}
        freq={props.freq}
        onAssigneesPress={onAssigneesPress}
      />
      <TouchableBox
        onPress={onToggle}
        disabled={
          inlineRoutineOccasion.taskExecution.routineOccasion.store?.myRole ===
          "READ_ONLY"
        }
      >
        <WidgetTeamScheduleRoutineIssueIndicator
          routineOccasion={inlineRoutineOccasion.taskExecution.routineOccasion}
        />
        <RoutineOccasionInlineStatusCircleRelay
          circleSize="medium"
          occasion={inlineRoutineOccasion.taskExecution.routineOccasion}
        />
      </TouchableBox>
    </TouchableBox>
  );
}
